import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { Avatar } from '@/components/ui/Avatar';
import { useToast } from '@/components/ui/Toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Menu, Sun, Moon, LogOut, PanelLeftClose, PanelLeftOpen, Power, Loader2 } from 'lucide-react';

interface HeaderProps {
  onMenuToggle?: () => void;
  onSidebarToggle?: () => void;
  sidebarCollapsed?: boolean;
}

export function Header({ onMenuToggle, onSidebarToggle, sidebarCollapsed }: HeaderProps) {
  const { tenant, logout } = useAuth();
  const { toast } = useToast();
  const [isLight, setIsLight] = useState(
    () => document.documentElement.classList.contains('light'),
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [restartOpen, setRestartOpen] = useState(false);
  // 'idle' = confirmação | 'restarting' = reiniciando + aguardando voltar
  const [restartPhase, setRestartPhase] = useState<'idle' | 'restarting'>('idle');
  const restarting = restartPhase === 'restarting';

  // Confirma o restart: chama o backend, depois polla /health/live até voltar ao ar
  // e dá feedback (toast). Sem window.confirm — modal no design do RAIOS.
  const handleConfirmRestart = useCallback(async () => {
    setRestartPhase('restarting');
    try {
      await api.restartSystem();
    } catch {
      // o backend encerra a conexão antes de responder — esperado.
    }
    // Espera o processo cair, depois polla até o /health/live responder de novo.
    await new Promise((r) => setTimeout(r, 4000));
    const deadline = Date.now() + 45000;
    let backOnline = false;
    while (Date.now() < deadline) {
      try {
        await api.healthLive();
        backOnline = true;
        break;
      } catch {
        await new Promise((r) => setTimeout(r, 2500));
      }
    }
    setRestartPhase('idle');
    setRestartOpen(false);
    toast(
      backOnline
        ? { message: 'Sistema reiniciado e de volta ao ar ✓', type: 'success' }
        : {
            message: 'O restart demorou mais que o esperado — confira o status do sistema.',
            type: 'warning',
            duration: 8000,
          },
    );
  }, [toast]);

  const toggleTheme = useCallback(() => {
    setIsLight((prev) => {
      const next = !prev;
      if (next) {
        document.documentElement.classList.add('light');
      } else {
        document.documentElement.classList.remove('light');
      }
      return next;
    });
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = () => setDropdownOpen(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [dropdownOpen]);

  return (
    <>
      {/* Skip to main content link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[999] focus:bg-raios-accent focus:text-white focus:px-4 focus:py-2 focus:rounded-md focus:text-sm focus:font-medium"
      >
        Ir para conteudo principal
      </a>
    <header
      className={cn(
        'sticky top-0 z-[200] flex items-center h-14 px-4 pt-[env(safe-area-inset-top)]',
        'bg-raios-surface/90 backdrop-blur-md border-b border-raios-border/50',
      )}
    >
      {/* Left: hamburger (mobile only) */}
      <button
        onClick={onMenuToggle}
        className="md:hidden text-raios-text-secondary hover:text-raios-text transition-colors mr-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40 rounded-md cursor-pointer"
        aria-label="Abrir menu"
      >
        <Menu className="w-5 h-5" aria-hidden="true" />
      </button>

      {/* Left: sidebar toggle (desktop only) */}
      {onSidebarToggle && (
        <button
          onClick={onSidebarToggle}
          className="hidden md:flex items-center justify-center p-1.5 rounded-md text-raios-muted hover:text-raios-text hover:bg-raios-elevated transition-colors mr-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40 cursor-pointer"
          aria-label={sidebarCollapsed ? 'Expandir sidebar' : 'Recolher sidebar'}
        >
          {sidebarCollapsed ? (
            <PanelLeftOpen className="w-[18px] h-[18px]" strokeWidth={1.8} aria-hidden="true" />
          ) : (
            <PanelLeftClose className="w-[18px] h-[18px]" strokeWidth={1.8} aria-hidden="true" />
          )}
        </button>
      )}

      {/* Center: RAIOS text on mobile */}
      <span className="md:hidden text-sm font-bold text-raios-text tracking-tight">RAIOS</span>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Right: restart (admin) + theme toggle + avatar dropdown */}
      <div className="flex items-center gap-2">
        {/* Restart do sistema — só admin. Botão "desligar/reiniciar" estilo SO. */}
        {tenant?.is_admin && (
          <button
            onClick={() => setRestartOpen(true)}
            disabled={restarting}
            className="p-2 rounded-lg text-raios-muted hover:bg-raios-danger/10 hover:text-raios-danger transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-danger/40 cursor-pointer disabled:opacity-60 disabled:cursor-wait"
            aria-label="Reiniciar o sistema"
            title={restarting ? 'Reiniciando…' : 'Reiniciar o sistema (admin)'}
          >
            <Power
              className={cn('w-[18px] h-[18px]', restarting && 'animate-pulse')}
              strokeWidth={1.8}
              aria-hidden="true"
            />
          </button>
        )}

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg text-raios-text-secondary hover:bg-raios-elevated hover:text-raios-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40 cursor-pointer"
          aria-label={isLight ? 'Modo escuro' : 'Modo claro'}
        >
          {isLight ? (
            <Moon className="w-[18px] h-[18px]" strokeWidth={1.8} aria-hidden="true" />
          ) : (
            <Sun className="w-[18px] h-[18px]" strokeWidth={1.8} aria-hidden="true" />
          )}
        </button>

        {/* Avatar dropdown */}
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDropdownOpen((prev) => !prev);
            }}
            className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40 cursor-pointer"
            aria-label="Menu do usuario"
            aria-expanded={dropdownOpen}
            aria-haspopup="true"
          >
            <Avatar size="sm" name={tenant?.name} />
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 rounded-lg bg-raios-surface shadow-raios-lg shadow-ghost-border z-[210] py-1">
              <div className="px-3 py-2 border-b border-raios-border/50">
                <p className="text-sm font-medium text-raios-text truncate">
                  {tenant?.name ?? 'Carregando...'}
                </p>
                <p className="text-2xs text-raios-muted truncate">{tenant?.email}</p>
              </div>
              <button
                onClick={logout}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-raios-text-secondary hover:bg-raios-elevated hover:text-raios-danger transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40 focus-visible:ring-inset cursor-pointer"
              >
                <LogOut className="w-4 h-4" aria-hidden="true" />
                Sair
              </button>
            </div>
          )}
        </div>
      </div>
    </header>

    {/* Modal de restart — design do RAIOS, com ciclo: confirmar → reiniciando → toast */}
    <Dialog
      open={restartOpen}
      onOpenChange={(o) => {
        if (!o && !restarting) setRestartOpen(false);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Power className="size-5 text-raios-danger" strokeWidth={2} aria-hidden="true" />
            Reiniciar o sistema
          </DialogTitle>
          <DialogDescription>
            O backend do RAIOS fica indisponível por cerca de 10 segundos e volta sozinho.
            Use quando algo travar ou entrar em loop.
          </DialogDescription>
        </DialogHeader>

        {restarting ? (
          <div className="flex items-center gap-3 py-2 text-sm text-raios-text-secondary">
            <Loader2 className="size-5 animate-spin text-raios-accent" aria-hidden="true" />
            Reiniciando… aguardando o sistema voltar ao ar.
          </div>
        ) : (
          <DialogFooter>
            <button
              type="button"
              onClick={() => setRestartOpen(false)}
              className="rounded-lg px-4 py-2 text-sm font-medium text-raios-text-secondary hover:bg-raios-elevated transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmRestart}
              className="inline-flex items-center gap-2 rounded-lg bg-raios-danger px-4 py-2 text-sm font-medium text-white hover:bg-raios-danger/90 transition-colors cursor-pointer"
            >
              <Power className="size-4" strokeWidth={2} aria-hidden="true" />
              Reiniciar agora
            </button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
